import './SearchableSelect.css';

import { useEffect, useId, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

/**
 * A type-to-filter combobox for picking one of many options — a plain
 * <select> becomes unusable once a list runs into the hundreds, since there's
 * no way to jump to an entry by name.
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel,
  disabled,
}: SearchableSelectProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className="searchable-select" ref={containerRef}>
      <input
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        autoComplete="off"
        className="searchable-select__input"
        disabled={disabled}
        placeholder={placeholder}
        role="combobox"
        type="text"
        value={isOpen ? query : selectedLabel}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          setIsOpen(true);
          setQuery('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setIsOpen(false);
            setQuery('');
          } else if (e.key === 'Enter' && filtered.length === 1) {
            selectOption(filtered[0]);
          }
        }}
      />
      {isOpen && (
        <ul className="searchable-select__list" id={listboxId} role="listbox">
          {filtered.length === 0 ? (
            <li className="searchable-select__empty">No matches</li>
          ) : (
            filtered.map((o) => (
              <li
                aria-selected={o.value === value}
                className="searchable-select__option"
                key={o.value}
                role="option"
                // onMouseDown (not onClick) fires before the input's blur, so
                // the outside-click handler above doesn't close the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOption(o);
                }}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
